// Copyright (c) StrangeBee 2024

import { Drawer } from 'antd';
import { DrawerProps } from 'antd/lib/drawer';
import React, { ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import { Footer } from '@Molecules/THDrawer/Footer/Footer';
import { classList } from '@Helpers/className';
import { ICONS } from '@Constants/iconsApp';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';

import './THDrawer.scss';

export interface THDrawerProps {
    title: DrawerProps['title'];
    width?: DrawerProps['width'];
    closable?: DrawerProps['closable'];
    maskClosable?: DrawerProps['maskClosable'];
    onClose?: DrawerProps['onClose'];
    destroyOnClose?: DrawerProps['destroyOnClose'];
    mask?: DrawerProps['mask'];
    getContainer?: DrawerProps['getContainer'];
    afterVisibleChange?: DrawerProps['afterVisibleChange'];
    footer?: (toggleDrawer: (...args: any[]) => any) => ReactNode;
    onChange?: (visible: boolean) => void;
    className?: string;
    defaultVisible?: boolean;
    triggerElem?: ReactElement;
    disableClose?: boolean;
    children: ReactElement | ((toggleDrawer: () => void) => ReactElement);
    visible?: boolean;
}

const THDrawer = ({
    title,
    width = '45vw',
    closable = true,
    maskClosable = false,
    destroyOnClose = true,
    onClose,
    mask = true,
    getContainer = 'body',
    footer,
    className,
    defaultVisible = false,
    triggerElem,
    afterVisibleChange,
    disableClose,
    onChange,
    visible,
    children,
}: THDrawerProps): ReactElement => {
    const [stateVisible, setStateVisible] = useState<boolean>(defaultVisible);
    const { direction } = useContext(LanguageSwitcherContext);

    const toggleDrawer = (): void => {
        setStateVisible(!stateVisible);
    };

    useEffect(() => {
        onChange?.(stateVisible);
    }, [stateVisible]); //eslint-disable-line

    useEffect(() => {
        if (visible !== undefined) {
            setStateVisible(visible);
        }
    }, [visible]);

    return (
        <>
            {triggerElem && React.cloneElement(triggerElem, { onClick: toggleDrawer })}

            <Drawer
                title={title}
                placement={direction === 'ltr' ? 'right' : 'left'}
                width={width}
                closable={closable}
                keyboard={!disableClose}
                maskClosable={maskClosable || !disableClose}
                destroyOnClose={destroyOnClose}
                mask={mask}
                closeIcon={
                    <span
                        className={classList('drawer-btn__close', { disable: disableClose })}
                        onClick={(evt) => disableClose && evt.stopPropagation()}
                    >
                        <ICONS.CloseIcon color="red" />
                    </span>
                }
                onClose={(e) => {
                    toggleDrawer();
                    onClose?.(e);
                }}
                visible={stateVisible}
                getContainer={getContainer}
                footer={footer && footer(toggleDrawer)}
                className={classList('th-drawer', className, { 'disable-close': disableClose })}
                afterVisibleChange={afterVisibleChange}
                push={false}
            >
                {typeof children === 'function' ? children(toggleDrawer) : children}
            </Drawer>
        </>
    );
};

THDrawer.Footer = Footer;

export default THDrawer;
